import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Toolbar, Button, Dialog, DialogContent, DialogActions, Typography, LinearProgress, Card, CardContent } from '@mui/material';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import ExtendedField from '../../components/text_field';
import SelectField from '../../components/fast_suggest_field';
import Helpers, { ApiHelper } from '../../helpers';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import axios from 'axios';
import DownloadButton from '../../components/download_button';
import FileSaverBase from 'filesaver.js-npm';
import Auth from '../../auth';
import EventTable from '../../components/event_table';
import SearchBox from '../../components/searchbox';
import ContentLoader from '../../components/content_loader';
import {getCompanyIds, nullableDateTemplate} from '../../helpers';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
// filesaver.js-npm ships no type declarations (matches reports.tsx's cast).
const FileSaver: any = FileSaverBase;

// NOTE: this component is named ShipmentOptions in the pre-migration .js source
// too (evidently copy-pasted from shipment_options.js as a starting point);
// preserved as-is since it's an internal identifier only (never rendered)
// and renaming it would be an opportunistic change outside this migration's
// type-only scope.
type CustomLookupsProps = {
    setTitle?: (title: string) => void;
};

// Audit-log filter is a constant with no dependencies on state/props; hoisted
// to module scope so its identity stays stable across renders (matches the
// class instance field, which was created once).
const eventFilter = {filters: [
      {field: "CustomerViewable", operator: 'eq', value: true},
      {filters: [
          {field: "EventTypeId", operator: 'eq', value: 72},
      ],
      logic: 'or'}
  ], logic: 'and'};

const ShipmentOptions = (props: CustomLookupsProps) => {
    const { setTitle } = props;

    // This component's state is a dynamic bag with computed keys
    // (`tableData${name}`, `searchTerm${name}`) that can't be split into
    // discrete useState hooks, so keep the single-object shape the class used.
    const [state, setState] = useState<any>({loading: true, tables: [], editorFields: [], showEdit: false, disableEdit: false, mapping: null, showError: false, showSuccess: false, showConfirm: false});

    // stateRef mirrors `this.state`: the table-config closures built once at
    // mount (getData/actions/edit/delete handlers) must read the *latest*
    // state at call time, not the render-captured snapshot. Kept in sync on
    // every render, synchronously with the committed state value.
    const stateRef = useRef(state);
    stateRef.current = state;

    // Partial-merge updater preserving the class's setState({...}) semantics.
    // Stable identity so the mount-time closures can rely on it.
    const patchState = useCallback((partial: any) => {
        setState((prev: any) => ({...prev, ...partial}));
    }, []);

    // Unused in this component but retained to match the class instance field
    // (and to keep the ApiHelper import wired the same way).
    const apiHelper = useRef(new ApiHelper());
    void apiHelper;

    const importInputRef = useRef<HTMLInputElement | null>(null);

    const onRowDelete = useCallback((row: any) => {
        patchState({row: row, showConfirm: true, disableConfirm: false, handleConfirm: deleteLookup, confirmMessage: 'Are you sure you want to delete this lookup?'});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const processTableEntry = useCallback((config: any, entry: any) => {
        for(let i = 0; i < config.Columns.length; i++){
            if(config.Columns[i] && config.Columns[i].Type === 'partner'){
                entry[`Column${i+1}Label`] = (stateRef.current.partnerOptions.find((r: any) => r.value === entry[`Column${i+1}`]) || {}).label || entry[`Column${i+1}`];
            }
        }
        return entry;
    }, []);

    const getTableData = useCallback(async (name: string, tableConfig: any) => {
        let tableData = stateRef.current["tableData" + name];
        if(!tableData){
            tableData = (await axios.get(Config.api + `/odata/Company/CustomLookups?$filter=TableName eq '${name}'`)).data.value;
            patchState({["tableData" + name]: tableData.map(processTableEntry.bind(null, tableConfig))});
        }
        if(stateRef.current["searchTerm" + name]){
            let split = stateRef.current["searchTerm" + name].toLowerCase().split(" ");
            let fields = ["Column1", "Column2", "Column3", "Column4", "Column5",
            "Column1Label", "Column2Label", "Column3Label", "Column4Label", "Column5Label", "ModifiedBy"
            ];
            tableData = tableData.filter((r: any) => split.every((s: string) => fields.filter(f => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0));
        }
        return tableData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clickImport = useCallback((tableName: string) => {
        patchState({importTable: tableName});
        importInputRef.current!.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchChange = useCallback((tableName: string, e: React.SyntheticEvent<HTMLInputElement>) => {
        let term = e.currentTarget.value;
        patchState({['searchTerm' + tableName]: term});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshTable = useCallback((tableName: string, extraState?: any) => {
        let tables = [...stateRef.current.tables];
        for(let i = 0; i < tables.length; i++){
            if(tables[i].Name === tableName){
                tables[i] = {...tables[i], refresh: !tables[i].refresh};
            }
        }
        patchState({tables, ...extraState});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const editRow = useCallback((tableName: string, row: any, newEntry?: boolean) => {
        let table = stateRef.current.tables.find((r: any) => r.Name === tableName);
        let config = table.LookupConfig;
        let editorFields: any[] = [];
        for(let i = 0; i < config.Columns.length; i++){
            if(config.Columns[i] && config.Columns[i].Title){
                let field: any = {name: `Column${i+1}`, label: config.Columns[i].Title, component: ExtendedField};
                if(config.Columns[i].Type === 'partner'){
                    field.component = SelectField;
                    field.options = stateRef.current.partnerOptions;
                }
                editorFields.push(field)
            }
        }
        patchState({showEdit: true, editorTitle: newEntry ? `New ${config.Title} Entry` : `Edit ${config.Title} Entry`, editorFields, editorValue: row});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const newLookup = useCallback((tableName: string) => {
        editRow(tableName, {TableName: tableName}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearAll = useCallback(async (tableName: string) => {
        patchState({disableConfirm: true});
        await axios.delete(Config.api + `/odata/Company/CustomLookups?table=${tableName}`);
        refreshTable(tableName, {showConfirm: false, ["tableData" + tableName]: []})
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const parseTable = useCallback((lookupConfig: any, canManageSettings: boolean) => {
        let config = JSON.parse(lookupConfig.Configuration);
        let columns: any[] = [];
        for(let i = 0; i < config.Columns.length; i++){
            if(config.Columns[i] && config.Columns[i].Title){
                let colId = `Column${i+1}`;
                if(config.Columns[i].Type === 'partner'){
                    colId += 'Label';
                }
                columns.push({id: colId, label: config.Columns[i].Title, sortable: true, filterable: true});
            }
        }
        columns.push({id: 'ModifiedBy', label: 'Modified By', width: '20em', hidden: true, sortable: true, filterable: true, toggleable: true});
        columns.push({id: 'LastModified', label: 'Last Modified', width: '15em', hidden: true, sortable: true, filterable: true, toggleable: true, template: nullableDateTemplate});
        let edit = editRow.bind(null, lookupConfig.Name);
        if(canManageSettings){
            columns.push({ command: 'commands', stopPropagation: 'true', width: '25em', template: (value: any, row: any) => <Fragment>
                <Button style={{marginRight: '1rem'}} variant='outlined' size='small' onClick={() => edit(row)}>Edit</Button>
                <Button variant='outlined' size='small' onClick={onRowDelete.bind(null, row)}>Delete</Button>
            </Fragment>});
        }
        let table = {
            Name: lookupConfig.Name,
            title: config.Title,
            LookupConfig: config,
            columns: columns,
            getData: getTableData.bind(null, lookupConfig.Name, config),
            keyField: 'Id',
            orderBy: columns[0].id,
            order: 'asc',
            pageSize: 10,
            pageSizes: [10, 20, 50, 100],
            actions: <div>
                {!(!config.Description) && <div style={{textAlign: 'left', marginBottom: '0.5rem'}}>{config.Description}</div>}
                <div className='tableActions' style={{textAlign: 'left'}}>
                {canManageSettings && <Fragment>
                        <Button variant='outlined' onClick={newLookup.bind(null, lookupConfig.Name)}>New</Button>
                        <DownloadButton variant='outlined' onClick={clickImport.bind(null, lookupConfig.Name)}>Import CSV</DownloadButton>
                        <DownloadButton variant='outlined' onClick={downloadLookups.bind(null, lookupConfig.Name, config.Title)}>Export CSV</DownloadButton>
                        <Button variant='outlined' onClick={() => patchState({showConfirm: true, confirmMessage: `Are you sure you want to remove all ${config.Title} entries?`, handleConfirm: clearAll.bind(null, lookupConfig.Name)})}>Clear All</Button>
                        </Fragment>}
                        <span style={{display: 'inline-block', verticalAlign: 'middle'}}>
                            <SearchBox placeholder='Search entries' onEnter={refreshTable.bind(null, lookupConfig.Name)} handleChange={searchChange.bind(null, lookupConfig.Name)}/>
                        </span>
                    </div>
            </div>
        };
        return table;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveLookup = useCallback(async (lookup: any) => {
        patchState({disableEdit: true});
        try{
            if(lookup.Id){
                lookup = (await axios.put(Config.api + `/odata/Company/CustomLookups(${lookup.Id})`, lookup)).data;
            }else{
                lookup = (await axios.post(Config.api + "/odata/Company/CustomLookups", lookup)).data;
            }
        }catch (e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            patchState({showError: true, error: error, disableEdit: false});
            return;
        }
        let tableConfig = stateRef.current.tables.find((r: any) => r.Name === lookup.TableName).LookupConfig;
        let tables = [...stateRef.current.tables];
        for(let i = 0; i < tables.length; i++){
            if(tables[i].Name === lookup.TableName){
                tables[i] = {...tables[i], refresh: !tables[i].refresh};
            }
        }
        lookup = processTableEntry(tableConfig, lookup);
        let tableData = stateRef.current["tableData" + lookup.TableName].slice();
        let existing = tableData.findIndex((r: any) => r.Id === lookup.Id);
        if(existing > -1){
            tableData[existing] = lookup;
        }else{
            tableData = [lookup, ...tableData];
        }
        patchState({tables, refreshLog: !stateRef.current.refreshLog, disableEdit: false, showEdit: false, editorValue: null, ["tableData" + lookup.TableName]: tableData});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadLookups = useCallback(async (tableName: string, tableTitle: string) => {
        var csv = (await axios.get(Config.api + '/odata/Company/Functions.ExportCustomLookups?tableName=' + tableName)).data.value;
        var blob = new Blob([csv], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, tableTitle + ".csv");
    }, []);

    const uploadLookups = useCallback(async (files?: FileList | null) => {
        if(!files || files.length === 0){
            return;
        }
        patchState({importing: true});
        try {
            let lookups = await (new Promise(function(resolve, reject){
                const reader = new FileReader();
                reader.readAsText(files[0]);
                reader.onload = function(){
                  resolve(reader.result);
                };
                reader.onerror = function(error) { reject(error); };
            }));
            let msg = (await axios.post(Config.api + "/odata/Company/Functions.ImportCustomLookups", {csv: lookups, table: stateRef.current.importTable})).data;
            refreshTable(stateRef.current.importTable, {['tableData' + stateRef.current.importTable]: null, showSuccess: true, successMessage: msg, refreshLog: !stateRef.current.refreshLog, importing: false});
        } catch (e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            patchState({showError: true, error: error, importing: false});
        }
        importInputRef.current!.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // deleteLookup is referenced by onRowDelete (via handleConfirm). Declared as
    // a stable function; onRowDelete captures this stable identity.
    async function deleteLookup() {
        patchState({disableConfirm: true});
        await axios.delete(Config.api + `/odata/Company/CustomLookups(${stateRef.current.row.Id})`);
        let tables = [...stateRef.current.tables];
        for(let i = 0; i < tables.length; i++){
            if(tables[i].Name === stateRef.current.row.TableName){
                tables[i] = {...tables[i], refresh: !tables[i].refresh};
            }
        }
        let tableData = stateRef.current["tableData" + stateRef.current.row.TableName].slice();
        let existing = tableData.findIndex((r: any) => r.Id === stateRef.current.row.Id);
        if(existing > -1){
            tableData.splice(existing, 1);
        }
        patchState({showConfirm: false, tables, ["tableData" + stateRef.current.row.TableName]: tableData});
    }

    useEffect(() => {
        const init = async () => {
            if(setTitle) {
                setTitle('Custom Lookups');
            }
            let tables = (await axios.get(Config.api + "/odata/Company/CustomLookupTables")).data.value;
            let canManageSettings = Auth.hasPermission('settings/manage');
            let partnerOptions = (await getCompanyIds()).map((r: any) => ({label: r.label, value: r.value.toString()}));
            patchState({partnerOptions, tables: tables.map((r: any) => parseTable(r, canManageSettings)), loading: false});
        };
        init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container spacing={2}>
        {state.loading && <Grid size={12}>
        <ContentLoader preserveAspectRatio='none' style={{height: '15rem', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
        </Grid>}
        {!state.loading && (!state.tables || state.tables.length === 0) && <Grid size={12}>
                <Card>
                    <CardContent>
                        There are no custom lookup tables configured for this account.
                    </CardContent>
                </Card>
            </Grid>}
            {state.tables.map((table: any) => <Grid key={table.Name} size={12}>
                <EnhancedTable config={table}/>
            </Grid>)}
            <Grid size={12}>
                <EventTable title="Audit Log" filter={eventFilter} refresh={state.refreshLog}/>
            </Grid>
            <input type="file" accept='.csv' id="importInput" ref={importInputRef} style={{display: "none"}} onChange={(e) => uploadLookups(e.target.files)}/>
              <Dialog open={state.showError} onClose={() => {patchState({showError: false})}}>
                  <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                  <DialogContent><Typography>{state.error}</Typography></DialogContent>
              </Dialog>
              <Dialog open={state.showSuccess} onClose={() => {patchState({showSuccess: false})}}>
                  <Toolbar className='lbtoolbar'>{'Success'}</Toolbar>
                  <DialogContent><Typography>{state.successMessage ? state.successMessage : 'Custom lookups were successfully imported.'}</Typography></DialogContent>
              </Dialog>
              <Dialog
                  maxWidth="sm"
                  open={state.showConfirm}
                  onClose={(event: any, reason: string) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                      if (state.showConfirm) return;
                    }
                    patchState({showConfirm: false});
                  }}>
                  <Toolbar className='lbtoolbar'>Confirm</Toolbar>
                  <DialogContent>
                    <Typography>{state.confirmMessage}</Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" onClick={() => patchState({showConfirm: false})} disabled={state.disableConfirm} color="primary">
                      No
                    </Button>
                    <Button variant="text" onClick={state.handleConfirm} disabled={state.disableConfirm} color="primary">
                      Yes
                    </Button>
                  </DialogActions>
                </Dialog>
                  <Dialog
                      maxWidth="sm"
                      open={state.showEdit}
                      onClose={(event: any, reason: string) => {
                        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                          if (state.disableEdit) return;
                        }
                        patchState({showEdit: false});
                      }}
                      fullWidth={true}>
                      {state.editorValue ?
                          <Form onSubmit={saveLookup}
                          initialValues={state.editorValue}
                          render={({ handleSubmit, pristine, invalid, values }: any) => (
                            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                            <Toolbar className='lbtoolbar'>{state.editorTitle}</Toolbar>
                          <DialogContent>
                          <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                            {state.editorFields.map((field: any, index: number) => <Field key={index} {...field}/>)}
                            </div>
                          </DialogContent>
                          <DialogActions>
                            <Button type='submit' variant="text" disabled={pristine || invalid || state.disableEdit} color="primary">
                              Save
                            </Button>
                          </DialogActions>
                          </form>
                          )}/>
                           :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }
                    </Dialog>
        </Grid>
    );
};

export default ShipmentOptions;
